import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MapPin, Home as HomeIcon, Building2, Calendar, Phone, ShieldCheck, Timer, PoundSterling, Smartphone, Tablet, Laptop, Monitor } from "lucide-react";
import { BUSINESS, AREAS } from "@/lib/business";
import RatingPill from "@/components/site/RatingPill";

export default function CallOut() {
  const hampshire = AREAS.filter(a => /Hampshire/i.test(a.region));
  const surrey = AREAS.filter(a => /Surrey/i.test(a.region));

  return (
    <div>
      <section className="hero-wash" data-testid="callout-hero">
        <div className="container-x py-12 sm:py-16">
          <p className="text-sm font-semibold text-[color:var(--icrack-magenta-deep)] uppercase tracking-wider">Mobile Repair Service</p>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold">Every repair, at your door.</h1>
          <p className="mt-3 max-w-2xl text-slate-600">Our mobile technician handles the <strong>full range</strong> of repairs on-site — phones, tablets, MacBooks, iMacs, Windows laptops and desktop PCs — across Eastleigh, Southampton, wider Hampshire and Surrey. <strong>No call-out charge, ever.</strong> Home / office labour is charged at a flat <strong>£{BUSINESS.callOutLabourRate}/hour</strong> + parts.</p>
          <div className="mt-6 flex flex-wrap gap-3 items-center">
            <Link to="/book"><Button className="h-11 rounded-xl btn-primary-magenta"><Calendar className="h-4 w-4 mr-2"/>Book Call-Out</Button></Link>
            <a href={`tel:${BUSINESS.phones[0].tel}`}><Button variant="outline" className="h-11 rounded-xl border-slate-200"><Phone className="h-4 w-4 mr-2" style={{color:"var(--icrack-magenta)"}}/>{BUSINESS.phones[0].display}</Button></a>
            <RatingPill />
          </div>
        </div>
      </section>

      <section className="section-pad" data-testid="callout-devices">
        <div className="container-x">
          <div className="max-w-2xl">
            <h2 className="font-display text-3xl font-bold">What we fix on a call-out visit</h2>
            <p className="mt-2 text-slate-600">Anything we repair in-store, we repair on-site — same tools, same warranty, same quality parts.</p>
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: Smartphone, title: "Phones", body: "iPhone & Android — screen, battery, charging port, camera, speaker, water damage." },
              { icon: Tablet, title: "iPads & Tablets", body: "Cracked screens, digitizers, batteries, home buttons, charging ports." },
              { icon: Laptop, title: "MacBooks & iMacs", body: "Keyboards, screens, batteries, SSD/RAM upgrades, logic-board diagnostics." },
              { icon: Monitor, title: "Windows Laptops & PCs", body: "Screen, keyboard, HDD/SSD, RAM, OS reinstall, virus removal, tune-ups." },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="rounded-2xl border border-slate-200 bg-white p-6">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: "var(--icrack-magenta-soft)" }}>
                  <Icon className="h-5 w-5" style={{ color: "var(--icrack-magenta-deep)" }} />
                </span>
                <h3 className="mt-4 font-semibold text-slate-900">{title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad bg-slate-50">
        <div className="container-x grid gap-6 md:grid-cols-3">
          {[
            { icon: PoundSterling, title: "£120/hr — no visit fee", body: "Call-outs are billed at a flat labour rate + parts. There is no separate visit / call-out fee." },
            { icon: Timer, title: "Fast on-site repairs", body: "Most jobs completed in 30–90 minutes at your home or office." },
            { icon: ShieldCheck, title: "3-month warranty", body: "Standard 3-month warranty on most parts and workmanship." },
          ].map(({ icon: Icon, title, body }) => (
            <div key={title} className="rounded-2xl border border-slate-200 bg-white p-6">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: "var(--icrack-magenta-soft)" }}>
                <Icon className="h-5 w-5" style={{ color: "var(--icrack-magenta-deep)" }} />
              </span>
              <h3 className="mt-4 font-semibold text-slate-900">{title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section-pad" data-testid="callout-coverage-list">
        <div className="container-x">
          <h2 className="font-display text-3xl font-bold">Areas we cover</h2>
          <p className="mt-2 text-slate-600">Tap a town to see local details, or check out our <Link to="/areas" className="link-magenta font-semibold">full areas directory</Link>.</p>
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <h3 className="font-semibold text-slate-900">Hampshire</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {hampshire.map(a => (
                  <Link key={a.slug} to={`/areas/${a.slug}`} className="inline-flex items-center gap-1.5 rounded-full border border-pink-200 bg-pink-50 px-3 py-1 text-sm text-slate-800 hover:bg-pink-100 transition-colors">
                    <MapPin className="h-3.5 w-3.5" style={{ color: "var(--icrack-magenta)" }} />{a.name}
                  </Link>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <h3 className="font-semibold text-slate-900">Surrey</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {surrey.map(a => (
                  <Link key={a.slug} to={`/areas/${a.slug}`} className="inline-flex items-center gap-1.5 rounded-full border border-pink-200 bg-pink-50 px-3 py-1 text-sm text-slate-800 hover:bg-pink-100 transition-colors">
                    <MapPin className="h-3.5 w-3.5" style={{ color: "var(--icrack-magenta)" }} />{a.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad bg-slate-50">
        <div className="container-x grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: "var(--icrack-magenta-soft)" }}>
              <HomeIcon className="h-5 w-5" style={{ color: "var(--icrack-magenta-deep)" }} />
            </span>
            <h3 className="mt-4 font-semibold text-slate-900">At home</h3>
            <p className="mt-2 text-slate-600 text-sm">Perfect for busy parents, remote workers, and anyone who can't easily get to Eastleigh. Book a slot that works for you.</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: "var(--icrack-magenta-soft)" }}>
              <Building2 className="h-5 w-5" style={{ color: "var(--icrack-magenta-deep)" }} />
            </span>
            <h3 className="mt-4 font-semibold text-slate-900">Business & fleet repairs</h3>
            <p className="mt-2 text-slate-600 text-sm">We repair company phones, tablets, laptops and Macs on-site. Ideal for small business fleets — invoice terms available.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
